import { useState } from 'react'
import { useApp } from './state/AppStore'
import { GameSetup } from './screens/GameSetup'
import { LiveGame } from './screens/LiveGame'
import { LiveView } from './screens/LiveView'
import { Manage } from './screens/Manage'
import { Stats } from './screens/Stats'
import { Admin } from './screens/Admin'
import { useAuth } from './auth/AuthProvider'
import { useLiveNow } from './state/useLiveWatch'
import { takeOverGame } from './state/takeOver'

type View = 'game' | 'stats' | 'manage' | 'admin'

export function App() {
  const { currentGameId, currentSeason, repo, snapshot, players, games, setCurrentGameId, reload } = useApp()
  const { isAdmin, member, signOut } = useAuth()
  const [view, setView] = useState<View>('game')
  const [watchDismissed, setWatchDismissed] = useState(false)
  const [takeOverError, setTakeOverError] = useState<string | null>(null)

  const live = useLiveNow()

  // The admin tab exists only for an admin; RLS refuses the writes regardless,
  // but there is no reason to show a door that will not open.
  const views: View[] = isAdmin ? ['game', 'stats', 'manage', 'admin'] : ['game', 'stats', 'manage']

  const ourTeamName = snapshot?.snapshot.teams.find((t) => t.is_us)?.name ?? 'Firehawks'
  const canScore = member?.role === 'scorer' || member?.role === 'admin'

  // The live game owns the whole screen: no nav chrome competing for the 768px.
  if (view === 'game' && currentGameId) {
    return <LiveGame gameId={currentGameId} onEnded={() => setView('game')} />
  }

  /**
   * Somebody is scoring and this device is not. On a Wednesday night that is
   * what almost everyone opening the app wants to see, so it is the screen they
   * get -- with a way out, because a bye-week bystander might be after Stats.
   */
  const watching =
    live.claim &&
    // A claim nobody has tapped for four hours is somebody who closed the app
    // last Wednesday, not a game in progress. It stays reachable from the bar
    // below, but it does not get to take over everyone's screen.
    !live.claim.is_stale &&
    !watchDismissed &&
    live.claim.game_id !== currentGameId

  const takeOver = async () => {
    if (!live.claim) return
    setTakeOverError(null)
    const result = await takeOverGame({
      repo,
      serverGameId: live.claim.game_id,
      fixture: snapshot?.snapshot.games.find((g) => g.id === live.claim!.game_id),
      seasonId: currentSeason.id,
      players,
      existingGames: games,
    })
    if (!result.ok) {
      setTakeOverError(result.error ?? 'Could not take over the game.')
      return
    }
    await reload()
    await setCurrentGameId(result.gameId!)
    setView('game')
  }

  if (view === 'game' && watching && live.claim) {
    return (
      <>
        {takeOverError && <div className="banner banner--error">{takeOverError}</div>}
        <LiveView
          gameId={live.claim.game_id}
          holderName={live.claim.holder_name}
          holderPlayerId={live.claim.holder_player_id}
          canScore={canScore}
          onTakeOver={canScore ? () => void takeOver() : undefined}
          onLeave={() => setWatchDismissed(true)}
        />
      </>
    )
  }

  return (
    <div className="app">
      <header className="app__header">
        <div className="app__brand">
          <span className="app__logo" aria-hidden="true" />
          <span className="app__brand-app">Hustle Stats:</span>
          {/* Our team gets the same weight the opponent gets on the fixture card. */}
          <span className="app__brand-team">{ourTeamName}</span>
        </div>
        <nav className="tabs">
          {views.map((v) => (
            <button
              key={v}
              type="button"
              className={'tabs__btn' + (view === v ? ' tabs__btn--on' : '')}
              onClick={() => setView(v)}
            >
              {v === 'game' ? 'New game' : v === 'stats' ? 'Stats' : v === 'manage' ? 'Manage' : 'Admin'}
            </button>
          ))}
        </nav>
        <div className="app__season muted small">
          {/* Grade name only: the full season name ("Winter 2026 - Wednesday Open
              Men Div 3") overflows the 1024px header. Full text in the tooltip. */}
          <span className="app__season-name" title={snapshot?.snapshot.season.name ?? currentSeason.name}>
            {snapshot?.snapshot.season.gradeName ?? snapshot?.snapshot.season.name ?? currentSeason.name}
          </span>
          <span className="app__storage" title={`Storage: ${repo.kind}`}>
            {repo.kind === 'indexeddb' ? 'IDB' : 'LS'}
          </span>
          <button
            type="button"
            className="app__signout"
            title={member?.display_name ? `Signed in as ${member.display_name}` : 'Sign out'}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Dismissed the live view but a game is still running: one line back to it. */}
      {live.claim && watchDismissed && (
        <button type="button" className="livebar" onClick={() => setWatchDismissed(false)}>
          <span className="livedot livedot--on" aria-hidden="true" />
          {live.claim.holder_name ?? 'Someone'} is scoring now. Watch the live tally.
        </button>
      )}

      <main className="app__body">
        {view === 'game' && <GameSetup onStarted={() => setView('game')} />}
        {view === 'stats' && <Stats />}
        {view === 'manage' && <Manage />}
        {view === 'admin' && <Admin />}
      </main>
    </div>
  )
}
